import React from 'react';
import { useMsal } from '@azure/msal-react';
import { useProfile } from '../../contexts/ProfileContext';
import { authFetch } from '../../utils/auth';
import { getFirstIncompleteStep } from '../../utils/onboardingNext';
import { getFirstNameFromClaims } from '../../utils/claims';
import Wordmark from '../../components/common/Wordmark';

export default function OnboardingIntro({ navigate }) {
  const { profile, refresh } = useProfile();
  const { instance, accounts } = useMsal();

  // First name from the Vipps-sourced ID token claims (only present if the
  // user consented to sharing it). Falls back to a generic greeting.
  const firstName = getFirstNameFromClaims(
    instance.getActiveAccount() || accounts[0]
  );
  
  // Check if user has already started the onboarding process
  const hasStarted = profile && (
    (profile.child && profile.child.birthYear) ||
    profile.kommune ||
    (profile.challenges && profile.challenges.length > 0) ||
    (profile.currentBenefits && profile.currentBenefits.length > 0)
  );
  
  const handleSkip = async () => {
    try {
      // Mark onboarding as skipped in profile
      await authFetch('/profile', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...profile,
          onboardingSkipped: true,
          profileComplete: true
        })
      });
      
      // Refresh profile context
      await refresh();
      
      // Navigate to dashboard
      navigate('/home', { replace: true });
    } catch (error) {
      console.error('Error skipping onboarding:', error);
      // Still navigate even if API call fails
      navigate('/home', { replace: true });
    }
  };

  const handleStart = () => {
    // Resume from where the user left off, or start from the beginning
    const nextStep = getFirstIncompleteStep(profile);
    if (nextStep === '/home') {
      // If profile is already complete, still allow them to go through the wizard
      navigate('/onboarding/child');
    } else {
      navigate(nextStep);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col items-center px-4">
      <div className="max-w-2xl w-full">
        {/* Logo header */}
        <div className="pt-8 pb-4">
          <Wordmark size="lg" />
        </div>

        {/* Header */}
        <div className="text-center mb-12 pt-8">
          <h1 className="text-3xl font-bold text-text-primary mb-4">
            {firstName ? `Velkommen til Coord, ${firstName}!` : 'Hei, og velkommen!'}
          </h1>
          
          <p className="text-lg text-text-secondary leading-relaxed">
            Vi hjelper familier med å finne de støtteordningene og rettighetene dere har krav på. 
            For å gi deg den beste mulige hjelpen, ønsker vi å lære litt om din situasjon.
          </p>
        </div>

        {/* Information collection explanation */}
        <div className="bg-white rounded-2xl p-8 shadow-sm border border-border-light mb-8">
          <h2 className="text-xl font-semibold text-text-primary mb-6 flex items-center">
            <svg className="w-6 h-6 text-primary mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Hvorfor samler vi denne informasjonen?
          </h2>
          
          <div className="space-y-4 text-text-secondary">
            <div className="flex items-start">
              <div className="w-2 h-2 bg-primary rounded-full mt-2 mr-3 flex-shrink-0"></div>
              <p>
                <strong>Personlig tilpasset veiledning:</strong> Vi gir deg råd som passer din familiesituasjon, 
                barnets alder, diagnoser og utfordringer.
              </p>
            </div>
            
            <div className="flex items-start">
              <div className="w-2 h-2 bg-primary rounded-full mt-2 mr-3 flex-shrink-0"></div>
              <p>
                <strong>Relevante forslag:</strong> Basert på din situasjon kan vi foreslå hvilke støtteordninger 
                som er mest aktuelle for dere å søke på først.
              </p>
            </div>
            
            <div className="flex items-start">
              <div className="w-2 h-2 bg-primary rounded-full mt-2 mr-3 flex-shrink-0"></div>
              <p>
                <strong>Bedre samtaler:</strong> I alle fremtidige samtaler med Coord-assistenten vil den 
                huske din situasjon og kunne gi mer presise svar.
              </p>
            </div>
          </div>
        </div>

        {/* Privacy and transparency */}
        <div className="bg-primary-50 border border-primary-200 rounded-xl p-6 mb-8">
          <div className="flex items-start">
            <svg className="w-6 h-6 text-primary mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            <div>
              <h3 className="font-semibold text-text-primary mb-2">Denne informasjonen lagres sikkert</h3>
              <p className="text-sm text-text-secondary mb-3">
                All informasjon du deler med oss lagres kryptert og sikkert. Du bestemmer selv hva du vil dele, 
                og du kan når som helst se, redigere eller slette dine lagrede opplysninger.
              </p>
              <p className="text-sm text-primary-700 font-medium">
                Du finner all lagret informasjon under "Coord minner" i kontomenyen din.
              </p>
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex flex-col sm:flex-row gap-4">
          <button
            onClick={handleStart}
            className="flex-1 bg-primary hover:bg-primary-600 text-white font-semibold py-4 px-8 rounded-xl transition-all duration-200 hover:shadow-lg active:transform active:scale-95 flex items-center justify-center"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
            {hasStarted ? 'Fortsett der du slapp' : 'La oss begynne'}
          </button>
          
          <button
            onClick={handleSkip}
            className="sm:w-auto bg-background-card hover:bg-neutral-100 text-text-secondary border border-border-light font-medium py-4 px-6 rounded-xl transition-all duration-200 hover:shadow-sm active:transform active:scale-95"
          >
            Hopp over - gjør senere
          </button>
        </div>
        
        <p className="text-center text-sm text-text-muted mt-6">
          Du kan når som helst avslutte og fortsette senere. All informasjon lagres underveis.
        </p>
      </div>
    </div>
  );
}